import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from 'api'
import { useAuth } from 'context/AuthContext'
import { parseError } from 'utils/error-handler'
import { CreatePaymentFeeRequest, Currency, PaymentProvider, VerifyPinRequest } from '../../generated-api'

export const usePaymentFees = () => {
    const { getAccessToken } = useAuth()

    const query = useQuery(['payment-fees'], async () =>
        apiClient.PaymentFeeControllerApi.getPaymentFees({
            headers: { Authorization: `Bearer ${await getAccessToken()}` },
        }).catch(parseError)
    )

    return query
}

export const useActiveFeeForLocation = (
    locationId: number,
    paymentProvider: PaymentProvider,
    paymentMethod: string,
    currency: Currency
) => {
    const { getAccessToken } = useAuth()

    const query = useQuery(
        ['payment-fee-active', locationId, paymentProvider, paymentMethod, currency],
        async () =>
            apiClient.PaymentFeeControllerApi.getActiveFeeForLocationOrGlobal(
                {
                    locationId,
                    paymentProvider,
                    paymentMethod,
                    currency,
                },
                {
                    headers: { Authorization: `Bearer ${await getAccessToken()}` },
                }
            ).catch(parseError),
        {
            enabled: !!locationId && !!paymentProvider && !!paymentMethod && !!currency,
        }
    )

    return query
}

export const useCreatePaymentFee = () => {
    const { getAccessToken } = useAuth()
    const queryClient = useQueryClient()

    const createPaymentFee = async (request: CreatePaymentFeeRequest) =>
        apiClient.PaymentFeeControllerApi.createPaymentFee(
            {
                createPaymentFeeRequest: request,
            },
            {
                headers: {
                    Authorization: `Bearer ${await getAccessToken()}`,
                    'Content-Type': 'application/json',
                },
            }
        ).catch(parseError)

    return useMutation({
        mutationFn: createPaymentFee,
        onSuccess: () => {
            queryClient.invalidateQueries(['payment-fees'])
            queryClient.invalidateQueries(['payment-fee-active'])
        },
    })
}

export const useVerifyGlobalFeePin = () => {
    const { getAccessToken } = useAuth()

    const verifyPin = async (pin: string) => {
        const request: VerifyPinRequest = { pin }
        return apiClient.PaymentFeeControllerApi.verifyGlobalFeePin(
            {
                verifyPinRequest: request,
            },
            {
                headers: {
                    Authorization: `Bearer ${await getAccessToken()}`,
                    'Content-Type': 'application/json',
                },
            }
        ).catch(parseError)
    }

    return useMutation({
        mutationFn: verifyPin,
    })
}
